export const moduleHelpData = {
  WarehouseTypeMaster: {
  title: "Warehouse Type Master",
  description:
    "The Warehouse Type Master module defines various types of warehouses such as Main Warehouse, Regional Hub, and Distribution Center. Each type is identified by a unique code and description, and can be set as active or inactive.",

  steps: [
    {
      title: "Step 1: View Warehouse Type List",
      details:
        "Upon opening the Warehouse Type Master, you will see a list of all existing warehouse types. Each row displays the Warehouse Type Code, Description, and Status.",
    },
    {
      title: "Step 2: Create New Warehouse Type",
      details:
        "Click the *New* button to clear the form fields and prepare for a new entry. Enter the Warehouse Type Code, Description, and select the Status (Active or Inactive).",
    },
    {
      title: "Step 3: Save Warehouse Type",
      details:
        "After entering all mandatory details, click *Save*. The new warehouse type record will be stored in the system.",
    },
    {
      title: "Step 4: Edit Existing Warehouse Type",
      details:
        "To modify an existing record, select it from the list and click *Edit*. Update the necessary fields and click *Save* to confirm the changes.",
    },
    {
      title: "Step 5: Deactivate or Delete Warehouse Type",
      details:
        "To deactivate a warehouse type, change its Status to *Inactive* and save. To permanently remove it (if unused elsewhere), click *Delete*.",
    },
    {
      title: "Step 6: Search or Filter Warehouse Types",
      details:
        "Use the *Search / Filter* option to quickly locate warehouse types based on Code, Description, or Status.",
    },
  ],

  fields: [
    {
      field: "Warehouse Type Code",
      required: true,
      hint: "Unique identifier for the warehouse type (e.g., MAIN, DIST, RETL). Must be unique.",
    },
    {
      field: "Description",
      required: true,
      hint: "Full descriptive name of the warehouse type (e.g., Main Warehouse, Distribution Center).",
    },
    {
      field: "Status",
      required: true,
      hint: "Select Active or Inactive to determine usability in system transactions.",
    },
  ],

  buttons: [
    { name: "New", hint: "Clears all fields for creating a new warehouse type." },
    { name: "Edit", hint: "Loads the selected record for editing." },
    { name: "Save", hint: "Saves a new or updated warehouse type record." },
    { name: "Delete", hint: "Deletes the record if it is not used elsewhere." },
    { name: "Search / Filter", hint: "Finds records based on Code, Description, or Status." },
  ],

  statusLegend: [
    { status: "🟢 Active", meaning: "The warehouse type is active and usable in system transactions." },
    { status: "🔴 Inactive", meaning: "The warehouse type is disabled and hidden from operational use." },
  ],

  examples: [
    { code: "MAIN", description: "Main Warehouse", status: "Active" },
    { code: "DIST", description: "Distribution Center", status: "Active" },
    { code: "RETL", description: "Retail Warehouse", status: "Inactive" },
  ],
},
WarehouseEnvironmentTypeMaster: {
  title: "Warehouse Environment Type Master",
  description:
    "The Warehouse Environment Type Master module defines various warehouse environment types such as Cold Storage, Dry Storage, and Temperature Controlled. Each environment type has a unique code and description, and can be activated or deactivated based on usage requirements.",

  steps: [
    {
      title: "Step 1: View Environment Type List",
      details:
        "When you open the Warehouse Environment Type Master, you’ll see a list of all defined environment types. Each record displays the Environment Code, Description, and Status.",
    },
    {
      title: "Step 2: Create New Environment Type",
      details:
        "Click the *New* button to clear the form and prepare for adding a new environment type. Enter the Environment Code, Description, and choose the Status (Active or Inactive).",
    },
    {
      title: "Step 3: Save Environment Type",
      details:
        "After entering all mandatory details, click *Save*. The new environment type will be created and added to the list.",
    },
    {
      title: "Step 4: Edit or Update Environment Type",
      details:
        "To update an existing environment type, select it from the list and click *Edit*. Modify the fields as required, then click *Save* to store the changes.",
    },
    {
      title: "Step 5: Deactivate or Delete Environment Type",
      details:
        "To deactivate an environment type, toggle the Status to *Inactive* and save. To remove it permanently (if not used in transactions), click *Delete*.",
    },
    {
      title: "Step 6: Search or Filter Environment Types",
      details:
        "Use the *Search / Filter* function to locate specific records by Environment Code, Description, or Status.",
    },
  ],

  fields: [
    {
      field: "Environment Code",
      required: true,
      hint: "Unique code identifying the environment type (e.g., COLD, DRY, TEMP). Cannot be duplicated.",
    },
    {
      field: "Description",
      required: true,
      hint: "Full descriptive name of the warehouse environment (e.g., Cold Storage, Dry Storage).",
    },
    {
      field: "Status",
      required: true,
      hint: "Indicates whether the environment type is Active (usable in transactions) or Inactive.",
    },
  ],

  buttons: [
    { name: "New", hint: "Clears the form to allow adding a new environment type." },
    { name: "Edit", hint: "Loads the selected record into edit mode for modification." },
    { name: "Save", hint: "Saves a new environment type or updates an existing one." },
    { name: "Delete", hint: "Removes the selected environment type if not used in transactions." },
    { name: "Search / Filter", hint: "Filters records based on Environment Code, Description, or Status." },
  ],

  statusLegend: [
    { status: "🟢 Active", meaning: "The environment type is currently active and available for use." },
    { status: "🔴 Inactive", meaning: "The environment type is disabled and cannot be used in new operations." },
  ],

  examples: [
    { code: "COLD", description: "Cold Storage", status: "Active" },
    { code: "DRY", description: "Dry Storage", status: "Active" },
    { code: "TEMP", description: "Temperature Controlled", status: "Inactive" },
  ],
},
LocationTypeMaster: {
  title: "Location Type Master",
  description:
    "The Location Type Master module is used to define and maintain various types of locations within a warehouse or organization, such as Storage, Dispatch, Receiving, or Quality Check areas. Each location type represents a classification for grouping locations that share a specific operational purpose.",

  steps: [
    {
      title: "Step 1: View Location Type List",
      details:
        "Upon opening the Location Type Master, you’ll see a list of existing location types in a table view. Each row shows the Location Type Code, Description, and Status. If no records exist, the message ⚠️ 'No Location Type data found!' will be displayed.",
    },
    {
      title: "Step 2: Create New Location Type",
      details:
        "Click *New Location Type* to open the entry form. Enter the Location Type Code, Description, and choose the Status (Active or Inactive).",
    },
    {
      title: "Step 3: Save Location Type",
      details:
        "After entering all mandatory details, click *Save*. The new location type will be added to the system. By default, new records are set as Active.",
    },
    {
      title: "Step 4: Edit Existing Location Type",
      details:
        "To modify a location type, select it from the list. The form opens in Edit Mode — make the necessary changes and click *Save* to update the record.",
    },
    {
      title: "Step 5: Deactivate (Soft Delete) Location Type",
      details:
        "To deactivate a record, click *Delete (Inactive Mode)*. This will mark the location type as Inactive without permanently removing it.",
    },
    {
      title: "Step 6: Search or Filter Records",
      details:
        "Use the *Search Filters* on the list screen to locate specific records by Location Type Code, Description, or Status.",
    },
  ],

  fields: [
    {
      field: "Location Type Code",
      required: true,
      hint: "A unique short code to identify the location type (e.g., STG, DSP). Must be unique and mandatory.",
    },
    {
      field: "Location Type Description",
      required: true,
      hint: "Detailed descriptive name for the location type (e.g., Storage, Dispatch, Receiving).",
    },
    {
      field: "Status",
      required: false,
      hint: "Indicates whether the location type is Active or Inactive. Defaults to Active for new records.",
    },
  ],

  buttons: [
    { name: "Save", hint: "Saves a new or updated location type record." },
    { name: "Clear", hint: "Clears all fields in the form for new entry." },
    { name: "Close (X)", hint: "Closes the form and returns to the list view." },
    { name: "Delete (Inactive Mode)", hint: "Marks the selected location type as Inactive (soft delete)." },
    { name: "New Location Type", hint: "Opens a new form to create a new location type." },
  ],

  validationRules: [
    { rule: "All mandatory fields (marked with *) must be filled before saving." },
    { rule: "Location Type Code must be unique — duplicate entries are not allowed." },
    { rule: "Description cannot be blank." },
    { rule: "Status defaults to Active when creating a new record." },
  ],

  workflow: [
    {
      step: "Step 1: View or Search",
      details:
        "Go to the Location Type List screen. Use search filters to find a specific record.",
    },
    {
      step: "Step 2: Add New Location Type",
      details:
        "Click *New Location Type*, fill in the required details, and click *Save*.",
    },
    {
      step: "Step 3: Edit Existing Location Type",
      details:
        "Select a record to open it in Edit Mode, modify the necessary fields, and click *Save*.",
    },
    {
      step: "Step 4: Delete or Inactivate",
      details:
        "Click *Delete* to mark the record as Inactive. The record remains in the database for reference but is hidden from new operations.",
    },
  ],

  statusLegend: [
    { status: "🟢 Active", meaning: "The location type is currently in use and available for transactions." },
    { status: "🔴 Inactive", meaning: "The location type is disabled and cannot be selected for new operations." },
  ],

  examples: [
    { code: "STG", description: "Storage Area", status: "Active" },
    { code: "DSP", description: "Dispatch Area", status: "Active" },
    { code: "RCV", description: "Receiving Area", status: "Active" },
    { code: "QCH", description: "Quality Check Area", status: "Inactive" },
  ],
},

ObjectTypeMaster: {
  title: "Object Type Master",
  description:
    "The Object Type Master module is used to define and manage various object types within the warehouse system. Each object type represents a physical or logical object characterized by specific dimensions (Length, Width, Height) and a unit of measurement. This setup helps standardize objects across warehouse operations.",

  steps: [
    {
      title: "Step 1: View Object Type List",
      details:
        "When you open the Object Type Master, you’ll see a list of all existing object types with details such as Object Type, Length, Width, Height, Unit, and Status. If no records exist, the message ⚠️ 'No Object Type data found!' will appear.",
    },
    {
      title: "Step 2: Create New Object Type",
      details:
        "Click *New Object Type* to open the entry form. Enter details for Object Type, Dimensions (Length, Width, Height), select the Unit of measurement, and choose the Status (Active or Inactive).",
    },
    {
      title: "Step 3: Save Object Type",
      details:
        "After filling in all mandatory fields, click *Save*. The new object type will be created and displayed in the list view.",
    },
    {
      title: "Step 4: Edit Existing Object Type",
      details:
        "To update an object type, select a record from the list. The form opens in Edit Mode — make the necessary changes and click *Save* to update the record.",
    },
    {
      title: "Step 5: Deactivate (Soft Delete) Object Type",
      details:
        "To deactivate an object type, click *Delete (Inactive Mode)*. The record will be marked as Inactive but not permanently deleted.",
    },
    {
      title: "Step 6: Search or Filter Object Types",
      details:
        "Use the *Search Filters* on the list screen to locate records by Object Type, Dimensions, Unit, or Status.",
    },
  ],

  fields: [
    {
      field: "Object Type",
      required: true,
      hint: "Unique name or identifier of the object type (e.g., PALLET, BOX, CONTAINER).",
    },
    {
      field: "Object Length",
      required: true,
      hint: "Length of the object. Must be a valid numeric value.",
    },
    {
      field: "Object Width",
      required: true,
      hint: "Width of the object. Must be a valid numeric value.",
    },
    {
      field: "Object Height",
      required: true,
      hint: "Height of the object. Must be a valid numeric value.",
    },
    {
      field: "Object Unit",
      required: true,
      hint: "Unit of measurement (e.g., cm, m, inch). Must be selected before saving.",
    },
    {
      field: "Status",
      required: false,
      hint: "Active = usable in system transactions; Inactive = disabled. Defaults to Active when new.",
    },
  ],

  buttons: [
    { name: "Save", hint: "Saves a new or updated object type record." },
    { name: "Clear", hint: "Clears all form fields for new entry or re-entry." },
    { name: "Close (X)", hint: "Closes the form and returns to the list view." },
    { name: "Delete (Inactive Mode)", hint: "Marks the record as Inactive (soft delete) instead of removing it." },
    { name: "New Object Type", hint: "Opens a new form to create a new object type record." },
  ],

  validationRules: [
    { rule: "All fields marked with * are mandatory before saving." },
    { rule: "Duplicate Object Type names are not allowed." },
    { rule: "Length, Width, and Height must contain valid numeric values." },
    { rule: "Unit of measurement must be selected before saving." },
  ],

  workflow: [
    {
      step: "Step 1: View or Search Existing Object Types",
      details:
        "Go to the Object Type List screen. Use filters to search by Object Type, Dimensions, Unit, or Status.",
    },
    {
      step: "Step 2: Add New Object Type",
      details:
        "Click *New Object Type*, fill in required details, and click *Save* to create a new record.",
    },
    {
      step: "Step 3: Edit Existing Object Type",
      details:
        "Select a record from the list to open it in Edit Mode. Update the necessary fields and click *Save*.",
    },
    {
      step: "Step 4: Delete or Deactivate",
      details:
        "Click *Delete (Inactive Mode)* to mark the record as Inactive. It remains in the system but hidden from active use.",
    },
  ],

  statusLegend: [
    { status: "🟢 Active", meaning: "The object type is active and usable in system transactions." },
    { status: "🔴 Inactive", meaning: "The object type is disabled and hidden from operational use." },
  ],

  examples: [
    { type: "PALLET", length: "120", width: "100", height: "15", unit: "cm", status: "Active" },
    { type: "BOX", length: "60", width: "40", height: "35", unit: "cm", status: "Active" },
    { type: "CONTAINER", length: "250", width: "200", height: "220", unit: "cm", status: "Inactive" },
  ],
},

WarehouseMappingMaster: {
  title: "Warehouse Mapping Master",
  description:
    "The Warehouse Mapping module is used to define and manage mappings between Zones, Package Modes, Warehouses, and Bin Types. These mappings ensure proper linkage between operational warehouse units for accurate and efficient material handling.",

  steps: [
    {
      title: "Step 1: View Warehouse Mapping List",
      details:
        "When you open the Warehouse Mapping Master, you will see a list of existing mappings displayed in a tabular format. Each record shows Zone & Package Mode, Warehouse, Bin Type, and Status. If no records exist, the message ⚠️ 'No Warehouse Mapping data found!' is displayed.",
    },
    {
      title: "Step 2: Search or Filter Mappings",
      details:
        "Use the *Search Filters* provided at the top of the list to find specific mappings by Zone, Package Mode, Warehouse, Bin Type, or Status.",
    },
    {
      title: "Step 3: Create New Mapping",
      details:
        "Click *New Mapping* to open a blank form. Select the Zone & Package Mode, choose the Warehouse, and the Bin Type will automatically load based on the selected Warehouse ID. Set the Status (Active/Inactive) as needed.",
    },
    {
      title: "Step 4: Save Mapping",
      details:
        "After completing all mandatory fields, click *Save*. The mapping will be created and added to the list view.",
    },
    {
      title: "Step 5: Edit Existing Mapping",
      details:
        "To update a mapping, click a row in the list. The record opens in Edit Mode — make the necessary changes and click *Save*.",
    },
    {
      title: "Step 6: Delete Mapping",
      details:
        "To permanently remove a mapping, click *Delete*. This action cannot be undone and will completely remove the mapping record from the database.",
    },
  ],

  fields: [
    {
      field: "Zone & Package Mode",
      required: true,
      hint: "Select a valid Zone and Package Mode combination from the dropdown list. This is a mandatory field.",
    },
    {
      field: "Warehouse",
      required: true,
      hint: "Select the warehouse to be linked with the chosen Zone & Package Mode.",
    },
    {
      field: "Bin Type",
      required: true,
      hint: "Auto-loaded field based on the selected Warehouse ID. Ensures mapping consistency.",
    },
    {
      field: "Status",
      required: false,
      hint: "Indicates whether the mapping is Active or Inactive.",
    },
  ],

  buttons: [
    { name: "Save", hint: "Saves a new or updated warehouse mapping record." },
    { name: "Clear", hint: "Clears all form fields for new data entry." },
    { name: "Delete", hint: "Permanently deletes the selected mapping record. This cannot be undone." },
    { name: "Close (X)", hint: "Closes the form and returns to the list view without saving." },
    { name: "New Mapping", hint: "Opens a blank form for creating a new warehouse mapping." },
  ],

  validationRules: [
    { rule: "All fields marked with * are mandatory before saving." },
    { rule: "Duplicate combinations of Zone, Package Mode, and Warehouse are not allowed." },
    { rule: "Bin Type must correspond to the selected Warehouse ID." },
    { rule: "System displays an alert if mandatory fields are left blank." },
  ],

  workflow: [
    {
      step: "Step 1: View or Search Mapping",
      details:
        "Open the Warehouse Mapping List screen to view existing records. Use filters to search by Zone, Package Mode, Warehouse, Bin Type, or Status.",
    },
    {
      step: "Step 2: Create New Mapping",
      details:
        "Click *New Mapping*, fill in all required details, and click *Save* to create a new record.",
    },
    {
      step: "Step 3: Edit Existing Mapping",
      details:
        "Select a mapping record from the list to open it in Edit Mode. Make necessary updates and click *Save*.",
    },
    {
      step: "Step 4: Delete Mapping",
      details:
        "Click *Delete* to permanently remove the mapping record. This action cannot be undone.",
    },
  ],

  statusLegend: [
    { status: "🟢 Active", meaning: "The mapping is active and available for use in warehouse transactions." },
    { status: "🔴 Inactive", meaning: "The mapping is inactive and not used in system operations." },
  ],

  examples: [
    {
      zonePackageMode: "ZONE-A / PALLETIZED",
      warehouse: "Main Warehouse",
      binType: "Standard Bin",
      status: "Active",
    },
    {
      zonePackageMode: "ZONE-B / BOXED",
      warehouse: "Distribution Center",
      binType: "Small Bin",
      status: "Active",
    },
    {
      zonePackageMode: "ZONE-C / MIXED",
      warehouse: "Cold Storage",
      binType: "Chilled Bin",
      status: "Inactive",
    },
  ],
},

  WarehouseMaster: {
    title: "Warehouse Master",
    description:
      "The Warehouse Master module allows you to create, edit, and manage all warehouses in your company, including environment and type settings.",

    steps: [
      {
        title: "Step 1: View Warehouse List",
        details:
          "When you open Warehouse Master, you'll first see the list of existing warehouses. Each row displays key details like Code, Description, Warehouse Type, and Status.",
      },
      {
        title: "Step 2: Create New Warehouse",
        details:
          "Click the *New* button to open the warehouse entry screen. Here you can input warehouse details such as Code, Description, Warehouse Type, Environment Type, WIP Flag, and Status.",
      },
      {
        title: "Step 3: Save Warehouse Details",
        details:
          "After entering all mandatory fields, click *Save*. The new warehouse will be created and the screen automatically switches to **Update** mode, allowing further edits if needed.",
      },
      {
        title: "Step 4: Update or Modify Warehouse",
        details:
          "To change any warehouse details, edit the fields and click *Update*. The system records who made the changes and when.",
      },
      {
        title: "Step 5: Delete or Deactivate Warehouse",
        details:
          "If a warehouse is no longer needed, click *Delete* to deactivate it. The record remains in the system but marked as inactive.",
      },
      {
        title: "Step 6: Return to Warehouse List",
        details:
          "Click **Back to List** to return to the warehouse overview page, where you can see all warehouses and perform further actions.",
      },
    ],

    fields: [
      { field: "Code", required: true, hint: "Short unique code (e.g., RMWH, FGWH)." },
      { field: "Description", required: true, hint: "Full human-readable name of the warehouse." },
      { field: "Warehouse Type", required: true, hint: "Select from master list (Raw Material, Finished Goods, etc.)." },
      { field: "Environment Type", required: true, hint: "Select environment (Ambient, Cold, Dry, etc.)." },
      { field: "WIP Flag", required: false, hint: "Mark true if this warehouse stores Work-In-Progress material." },
      { field: "Status", required: false, hint: "Active = true / Inactive = false." },
      { field: "Company ID", required: true, hint: "Auto-filled in multi-company setups." },
    ],

    buttons: [
      { name: "New", hint: "Start adding a new warehouse." },
      { name: "Save", hint: "Save a newly created warehouse record." },
      { name: "Update", hint: "Modify and save existing warehouse details." },
      { name: "Delete", hint: "Deactivate the selected warehouse record." },
      { name: "Back to List", hint: "Return to the warehouse list view." },
    ],
  },

  BinTypeMaster: {
    title: "Bin Type Master",
    description:
      "The Bin Type Master module enables you to define, edit, and manage different types of storage bins in your warehouse. This includes specifying hierarchical warehouse levels and advanced attributes for each bin type.",
    steps: [
      {
        title: "Step 1: View Bin Type List",
        details:
          "Opening Bin Type Master displays a list of current bin types along with Code, Description, hierarchical levels, and key attributes.",
      },
      {
        title: "Step 2: Create New Bin Type",
        details:
          "Click *New* to open the entry form. Fill in details such as Code, Description,  warehouse levels, and advanced bin settings like Limit Type, Max Different SKU's, Horizontal Object Gap, and stackability.",
      },
      {
        title: "Step 3: Save Bin Type Details",
        details:
          "Complete all mandatory fields and click *Save*. The new bin type record will be created and available for further updates.",
      },
      {
        title: "Step 4: Update or Modify Bin Type",
        details:
          "Edit bin type details and advanced attributes as necessary. Click *Update* to save changes, with all modifications tracked.",
      },
      {
        title: "Step 5: Delete or Deactivate Bin Type",
        details:
          "To deactivate a bin type, click *Delete*. The record remains but is marked as inactive.",
      },
      {
        title: "Step 6: Return to Bin Type List",
        details:
          "Click **Back to List** to review or manage all existing bin types.",
      },
    ],
    fields: [
      { field: "Code", required: true, hint: "Short unique code for the bin type (e.g., SMALL, LARGE, PALLET)." },
      { field: "Description", required: true, hint: "Full name or description for the bin type." },
      { field: "Limit Type", required: false, hint: "Constraint on contents - select NONE, WEIGHT, COUNT, or VOLUME." },
      { field: "Max Different SKU'S", required: false, hint: "Maximum number of distinct SKUs allowed in this bin." },
      { field: "Horizontal Object Gap (cm)", required: false, hint: "Gap between objects stored horizontally in the bin." },
      { field: "Location Code Formate", required: false, hint: "Custom code format for bin location identification." },
      { field: "Allow Multiple Items", required: false, hint: "Allow more than one item in the bin (true/false)." },
      { field: "Is Stackable", required: false, hint: "Indicates if bin contents can be stacked (true/false)." },
      { field: "Status", required: false, hint: "Active = true / Inactive = false." },
      { field: "Company ID", required: true, hint: "Auto-filled for multi-company support." },
      { field: "Warehouse Levels", required: true, hint: "Enable each level and provide the name, e.g., Zone, Aisle." },
    ],
    buttons: [
      { name: "New", hint: "Start entering a new bin type record." },
      { name: "Save", hint: "Save bin type details to the master." },
      { name: "Update", hint: "Edit and save changes to an existing bin type." },
      { name: "Delete", hint: "Mark selected bin type as inactive." },
      { name: "Back to List", hint: "Return to the bin type overview screen." },
    ],
  },

  StorageLocationMaster: {
    title: "Storage Location Master",
    description:
      "This module allows you to define and manage individual bin locations within a warehouse. After creation, only location-specific dimensions and restrictions can be modified.",
    steps: [
      {
        title: "Step 1: Select Warehouse and Bin Type",
        details:
          "Choose the warehouse, bin type, and location type. These define the structural context and cannot be changed later.",
      },
      {
        title: "Step 2: Generate Location Codes",
        details:
          "System auto-generates location codes (e.g., w001-Z1, w001-Z2) based on bin type and level configuration.",
      },
      {
        title: "Step 3: Enter Dimensions and Restrictions",
        details:
          "For each location code, enter Width, Length, Height, Max Weight, Max Volume, Max Item Count, and toggle Mixed Items and Mixed Batches.",
      },
      {
        title: "Step 4: Save Storage Locations",
        details:
          "Click *Save* to finalize the configuration. Structural fields are locked; only location-level data can be edited later.",
      },
      {
        title: "Step 5: Modify Location Details",
        details:
          "Click the eye icon to view or edit a location. You can update dimensions and restrictions only.",
      },
      {
        title: "Step 6: Return to Location List",
        details:
          "Click **Back to List** to manage or review all configured bin locations.",
      },
    ],
    fields: [
      { field: "Warehouse", required: true, editable: false, hint: "Selected warehouse (e.g., w001)." },
      { field: "Bin Type", required: true, editable: false, hint: "Bin type used for location generation." },
      { field: "Location Type", required: true, editable: false, hint: "Type of location (e.g., BIN, RACK)." },
      { field: "Location Code", required: true, editable: false, hint: "Auto-generated code for each bin location." },
      { field: "Width (cm)", required: true, editable: true, hint: "Editable after save." },
      { field: "Length (cm)", required: true, editable: true, hint: "Editable after save." },
      { field: "Height (cm)", required: true, editable: true, hint: "Editable after save." },
      { field: "Max Weight (kg)", required: false, editable: true, hint: "Editable after save." },
      { field: "Max Volume", required: false, editable: true, hint: "Editable after save." },
      { field: "Max Item Count", required: false, editable: true, hint: "Editable after save." },
      { field: "Allow Mixed Items", required: false, editable: true, hint: "Editable after save." },
      { field: "Allow Mixed Batches", required: false, editable: true, hint: "Editable after save." },

      { field: "Company ID", required: true, editable: false, hint: "Auto-filled for multi-company support." },
    ],
    buttons: [
      { name: "New", hint: "Start entering a new storage location set." },
      { name: "Save", hint: "Save all location entries to the master." },
      { name: "Update", hint: "Edit dimensions and restrictions for existing locations." },
      { name: "Back to List", hint: "Return to the location overview screen." },
    ],
    exampleRecordSet: {
      Warehouse: "w001",
      BinType: "b1",
      LocationType: "BIN",
      Locations: [
        {
          LocationCode: "w001-Z1",
          Width: 34,
          Length: 34,
          Height: 34,
          MaxWeight: 0,
          MaxVolume: 0,
          MaxItemCount: 0,
          AllowMixedItems: false,
          AllowMixedBatches: false
        },
        {
          LocationCode: "w001-Z2",
          Width: 34,
          Length: 34,
          Height: 34,
          MaxWeight: 0,
          MaxVolume: 0,
          MaxItemCount: 0,
          AllowMixedItems: false,
          AllowMixedBatches: false
        },
        {
          LocationCode: "w001-Z3",
          Width: 34,
          Length: 34,
          Height: 34,
          MaxWeight: 0,
          MaxVolume: 0,
          MaxItemCount: 0,
          AllowMixedItems: false,
          AllowMixedBatches: false
        },
        {
          LocationCode: "w001-Z4",
          Width: 34,
          Length: 34,
          Height: 34,
          MaxWeight: 0,
          MaxVolume: 0,
          MaxItemCount: 0,
          AllowMixedItems: false,
          AllowMixedBatches: false
        },
        {
          LocationCode: "w001-Z5",
          Width: 34,
          Length: 34,
          Height: 34,
          MaxWeight: 0,
          MaxVolume: 0,
          MaxItemCount: 0,
          AllowMixedItems: false,
          AllowMixedBatches: false
        }
      ]
    }
  }

};
